import { createContext, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router';
import qs, { ParseOptions } from 'query-string';

import { FCWithChildren } from 'common/types';

const ARRAY_FORMAT: ParseOptions['arrayFormat'] = 'bracket';

type Filters = { q: string; statuses?: string[] | null };

const defaults: Filters = {
  q: '',
};

interface ContextType {
  filters: Filters;
  updateFilters: (newFilters: Filters) => void;
}

export const FiltersContext = createContext<ContextType>({
  filters: defaults,
  updateFilters: () => {},
});

const searchToFilters = (
  search: string,
  validStatuses: string[] = [],
): Filters => {
  const filters = qs.parse(search, { arrayFormat: ARRAY_FORMAT });

  const statuses = Array.isArray(filters.statuses)
    ? filters.statuses.filter((status) => validStatuses.includes(status))
    : undefined;

  return {
    q: filters.q?.toString() ?? '',
    statuses,
  };
};

interface FiltersProps {
  validStatuses?: string[];
}

export const FiltersProvider: FCWithChildren<FiltersProps> = ({
  children,
  validStatuses,
}) => {
  const history = useHistory();

  const filters = useMemo(
    () => searchToFilters(history.location.search, validStatuses),
    [history.location.search, validStatuses],
  );

  const updateFilters = useCallback(
    (newFilters: Filters) => {
      history.push({
        search: qs.stringify(
          { ...newFilters },
          {
            arrayFormat: ARRAY_FORMAT,
            skipEmptyString: true,
            skipNull: true,
          },
        ),
      });
    },
    [history],
  );

  return (
    <FiltersContext.Provider
      value={{
        filters,
        updateFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export function useFilters() {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error('useFilters can only be used inside of a FiltersProvider');
  }

  return context;
}
